<template>
  <div class="show_all_content_wrapper">
    <main>
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("SIDENAV.homeSections.title") }}</h5>
        </div>
      </div>

      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :headers="tableHeaders"
        :items="tableRows"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>

        <template v-slot:[`item.id`]="{ index }">
          <p>
            {{
              (paginations.current_page - 1) * paginations.items_per_page +
              index +
              1
            }}
          </p>
        </template>

        <template v-slot:[`item.order`]="{ item }">
          <input
            type="number"
            class="form-control order_input"
            min="1"
            v-model.number="item.order"
            :disabled="savingOrderId === item.id"
            @blur="updateSectionOrder(item)"
            @keyup.enter="$event.target.blur()"
          />
        </template>

        <template v-slot:[`item.title`]="{ item }">
          <p>{{ displayTitle(item) }}</p>
        </template>

        <template v-slot:[`item.name`]="{ item }">
          <p>{{ item.name || "-" }}</p>
        </template>

        <template v-slot:[`item.image`]="{ item }">
          <div class="table_image_wrapper">
            <h6 class="text-danger" v-if="!item.image">
              {{ $t("TABLES.noData") }}
            </h6>
            <button class="my-1" @click="showImageModal(item.image)" v-else>
              <img
                class="rounded"
                :src="item.image"
                width="60"
                height="60"
                alt=""
              />
            </button>
          </div>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.edit") }}</span>
              </template>
              <button class="btn_edit" @click="editItem(item)">
                <i class="fal fa-edit"></i>
              </button>
            </a-tooltip>
          </div>
        </template>

        <template v-slot:top>
          <image-modal
            v-if="dialogImage"
            :modalIsOpen="dialogImage"
            :modalImage="selectedItemImage"
            @toggleModal="dialogImage = !dialogImage"
          />
        </template>
      </v-data-table>

      <template v-if="paginations.last_page > 1">
        <div class="pagination_container text-center mt-5">
          <v-pagination
            circle
            v-model="paginations.current_page"
            :length="paginations.last_page"
            :total-visible="6"
            @input="updateRouterQueryParam($event)"
            :prev-icon="
              getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
            "
            :next-icon="
              getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
            "
          />
        </div>
      </template>
    </main>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllHomeSections",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
  },

  data() {
    return {
      loading: false,
      savingOrderId: null,
      originalOrders: {},
      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "id",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("MESSAGES.sort"),
          value: "order",
          align: "center",
          width: "120",
          sortable: false,
        },
        {
          text: this.$t("SIDENAV.homeSections.sectionTitle"),
          value: "title",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("SIDENAV.homeSections.sectionKey"),
          value: "name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.image"),
          value: "image",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("SIDENAV.questions.date"),
          value: "updated_at",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      dialogImage: false,
      selectedItemImage: null,
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
    };
  },

  watch: {
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
  },

  methods: {
    displayTitle(item) {
      if (this.getAppLocale === "ar") {
        return item.title_ar || item.title;
      }

      return item.title_en || item.title;
    },

    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },

    async setTableRows() {
      this.loading = true;

      try {
        const res = await this.$axios({
          method: "GET",
          url: "home-sections",
          params: {
            page: this.paginations.current_page,
          },
        });

        const rows = (res.data.data.data || []).map((item) => ({ ...item }));

        this.tableRows = rows.sort((a, b) => Number(a.order) - Number(b.order));
        this.originalOrders = {};
        this.tableRows.forEach((item) => {
          this.originalOrders[item.id] = Number(item.order);
        });

        this.paginations.last_page = res.data.data.meta.last_page;
        this.paginations.items_per_page = res.data.data.meta.per_page;
      } catch (error) {
        this.$message.error(error.response?.data?.message);
      } finally {
        this.loading = false;
      }
    },

    async updateSectionOrder(item) {
      const newOrder = Number(item.order);
      const previousOrder = this.originalOrders[item.id];

      if (!Number.isInteger(newOrder) || newOrder < 1) {
        item.order = previousOrder;
        this.$message.error(this.$t("VALIDATION.homeSectionOrder"));
        return;
      }

      if (newOrder === previousOrder) return;

      this.savingOrderId = item.id;

      const REQUEST_DATA = new FormData();
      REQUEST_DATA.append("title[ar]", item.title_ar || item.title || "");
      REQUEST_DATA.append("title[en]", item.title_en || item.title || "");
      REQUEST_DATA.append("order", newOrder);
      REQUEST_DATA.append("is_active", item.is_active === false ? 0 : 1);

      try {
        await this.$axios({
          method: "POST",
          url: `home-sections/${item.id}`,
          data: REQUEST_DATA,
        });

        this.$message.success(this.$t("MESSAGES.editedSuccessfully"));
        await this.setTableRows();
      } catch (error) {
        item.order = previousOrder;
        this.$message.error(error.response?.data?.message);
      } finally {
        this.savingOrderId = null;
      }
    },

    showImageModal(image) {
      this.dialogImage = true;
      this.selectedItemImage = image;
    },

    editItem(item) {
      this.$router.push({ path: `/home-sections/edit/${item.id}` });
    },
  },

  created() {
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
  },
};
</script>

<style scoped>
.order_input {
  width: 80px;
  min-height: 40px;
  margin: 0 auto;
  text-align: center;
}
</style>
